/**
 * O que o trabalhador respondeu e assinou antes da consulta (anamnese digital), na aba Anamnese.
 *
 * "Trazer para a anamnese" só preenche o que o médico ainda deixou em branco — nunca sobrescreve o
 * que ele já escreveu. A resposta é do trabalhador; a anamnese continua sendo do médico.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Collapse, Stack, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, Input as TrazerIcon } from '@mui/icons-material';

import anamneseDigitalService, { type RespostasParaOMedico } from '../../../services/anamneseDigitalService';
import {
  AlcoholUse,
  SmokingStatus,
  type UpdateMedicalRecordData,
} from '../../../types/medicalRecord';

const CAMPOS_DE_TEXTO = [
  'chiefComplaint',
  'medicalHistory',
  'currentMedications',
  'allergies',
  'previousJobs',
  'workAccidentHistory',
  'occupationalHistory',
  'familyHistory',
  'physicalActivityDetails',
] as const;

function vazio(valor: unknown): boolean {
  return valor === undefined || valor === null || (typeof valor === 'string' && valor.trim() === '');
}

function ehSmoking(valor: string | null): valor is SmokingStatus {
  return valor !== null && (Object.values(SmokingStatus) as string[]).includes(valor);
}

function ehAlcohol(valor: string | null): valor is AlcoholUse {
  return valor !== null && (Object.values(AlcoholUse) as string[]).includes(valor);
}

export interface AnamneseDoTrabalhadorPanelProps {
  appointmentId: number;
  data: UpdateMedicalRecordData;
  onChange: (data: UpdateMedicalRecordData) => void;
  disabled?: boolean;
}

export const AnamneseDoTrabalhadorPanel: React.FC<AnamneseDoTrabalhadorPanelProps> = ({
  appointmentId,
  data,
  onChange,
  disabled = false,
}) => {
  const [resposta, setResposta] = useState<RespostasParaOMedico | null>(null);
  const [aberto, setAberto] = useState(false);
  const [trazidos, setTrazidos] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;
    anamneseDigitalService
      .respostas(appointmentId)
      .then((r) => {
        if (!cancelado) setResposta(r);
      })
      // Sem permissão de prontuário ou sem anamnese: o painel simplesmente não aparece.
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, [appointmentId]);

  const linhas = useMemo(() => {
    if (!resposta || resposta.status !== 'respondida') return [];
    return resposta.questionario.flatMap((secao) =>
      secao.perguntas
        .map((p) => {
          const r = resposta.respostas[p.id];
          if (!r) return null;
          const valor =
            p.tipo === 'simnao'
              ? r.valor === 'sim'
                ? 'Sim'
                : 'Não'
              : p.tipo === 'opcoes'
                ? (p.opcoes?.find((o) => o.valor === r.valor)?.rotulo ?? r.valor)
                : r.valor;
          return { id: p.id, secao: secao.titulo, texto: p.texto, valor, detalhe: r.detalhe, destaque: r.valor === 'sim' };
        })
        .filter((l): l is NonNullable<typeof l> => l !== null)
    );
  }, [resposta]);

  if (!resposta) return null;

  if (resposta.status !== 'respondida') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {resposta.status === 'pendente'
          ? 'O trabalhador recebeu o questionário de saúde e ainda não respondeu.'
          : 'O trabalhador não respondeu o questionário de saúde. A recepção pode abri-lo no tablet.'}
      </Alert>
    );
  }

  const trazer = (): void => {
    const sugestao = resposta.sugestao;
    if (!sugestao) return;
    const novo: UpdateMedicalRecordData = { ...data };
    let quantos = 0;
    for (const campo of CAMPOS_DE_TEXTO) {
      const valor = sugestao[campo];
      if (valor && vazio(novo[campo])) {
        novo[campo] = valor;
        quantos += 1;
      }
    }
    if (ehSmoking(sugestao.smokingStatus) && vazio(novo.smokingStatus)) {
      novo.smokingStatus = sugestao.smokingStatus;
      quantos += 1;
    }
    if (ehAlcohol(sugestao.alcoholUse) && vazio(novo.alcoholUse)) {
      novo.alcoholUse = sugestao.alcoholUse;
      quantos += 1;
    }
    if (sugestao.physicalActivity !== null && vazio(novo.physicalActivity)) {
      novo.physicalActivity = sugestao.physicalActivity;
      quantos += 1;
    }
    onChange(novo);
    setTrazidos(quantos);
  };

  const sins = linhas.filter((l) => l.destaque).length;

  return (
    <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'success.light', borderRadius: 1, bgcolor: 'action.hover' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight="medium">
            Respondido pelo trabalhador
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {resposta.respondidaEm ? new Date(resposta.respondidaEm).toLocaleString('pt-BR') : ''}
            {resposta.origemDaResposta === 'tablet' ? ' · no tablet da recepção' : ' · pelo celular'}
            {resposta.temAssinatura ? ' · assinado' : ''} · {sins} {sins === 1 ? 'resposta "sim"' : 'respostas "sim"'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setAberto((a) => !a)} endIcon={aberto ? <ExpandLess /> : <ExpandMore />}>
            {aberto ? 'Esconder' : 'Ver respostas'}
          </Button>
          <Button size="small" variant="contained" startIcon={<TrazerIcon />} onClick={trazer} disabled={disabled}>
            Trazer para a anamnese
          </Button>
        </Stack>
      </Stack>

      {trazidos !== null && (
        <Alert severity={trazidos > 0 ? 'success' : 'info'} sx={{ mt: 1.5 }} onClose={() => setTrazidos(null)}>
          {trazidos > 0
            ? `${trazidos} ${trazidos === 1 ? 'campo preenchido' : 'campos preenchidos'} com as respostas. Revise antes de salvar.`
            : 'Os campos já estavam preenchidos — nada foi alterado.'}
        </Alert>
      )}

      <Collapse in={aberto}>
        <Stack spacing={0.75} sx={{ mt: 2 }}>
          {linhas.map((l) => (
            <Stack key={l.id} direction="row" spacing={1} alignItems="flex-start">
              <Chip size="small" label={l.valor} color={l.destaque ? 'warning' : 'default'} sx={{ minWidth: 48 }} />
              <Typography variant="body2">
                {l.texto}
                {l.detalhe ? <strong> — {l.detalhe}</strong> : null}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default AnamneseDoTrabalhadorPanel;
